import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import LocalStorageService from "./LocalStorageService";
import { subscribeTokenChanges } from "./authTokenEvents";
import {
  socketMessage,
  socketRead,
} from "../../features/orderChat/orderChatSlice";
import { socketOrderUpdate } from "../../features/orders/ordersSlice";
import {
  socketNotification,
  socketNotificationRead,
} from "../../features/notifications/notificationsSlice";
import {
  getNotifications,
  NOTIFICATIONS_PER_PAGE,
} from "../../features/notifications/notificationsActions";
import { playBeep } from "../../utils/playBeep";

// The shop channel (REACT_APP_WS_URL, …/ws/channels/shop/). One socket per
// logged-in customer, opened at the app root. The server pushes frames, the
// client never sends anything:
//   { type: "order_message",      payload: <message row> }
//   { type: "order_message_read", payload: { order_id, unread_count } }
//   { type: "order_update",       payload: { previous_status, order } }
//   { type: "notification",       payload: <notification row> }
//   { type: "notification_read",  payload: { ids, is_read, unread_count,
//                                            read_count, unread_by_type } }
// `order_update` fires only when the status of one of this customer's orders
// changes; `order` is one row of GET /orders/history/ and `previous_status`
// is null when the order just appeared (created visible, or left DRAFT).
// `notification` follows the `order_update` / `order_message` it is about
// (the backend guarantees that order) and is the one frame that beeps.

export const RECONNECT_BASE_MS = 2000;
export const RECONNECT_MAX_MS = 30000;
// Server close codes that must NOT trigger a reconnect: 4401 = no/invalid
// token (wait for the next successful refresh), 4403 = not a shop patient.
export const CLOSE_UNAUTHORIZED = 4401;
export const CLOSE_FORBIDDEN = 4403;

// 2s, 4s, 8s, … capped at 30s.
export const getReconnectDelay = (attempt) =>
  Math.min(RECONNECT_BASE_MS * 2 ** Math.max(0, attempt), RECONNECT_MAX_MS);

export default function useShopSocket() {
  const dispatch = useDispatch();
  const store = useStore();
  const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);
  // Bumped whenever the stored token changes (refresh here or in another
  // tab), which re-runs the connection effect with the new token.
  const [tokenVersion, setTokenVersion] = useState(0);
  // True once any socket of this hook has opened. The next open after that
  // is a reconnect (network, or token rotation), so page 1 of the bell is
  // refetched to pick up anything pushed while offline.
  const hasOpenedRef = useRef(false);

  useEffect(
    () =>
      subscribeTokenChanges(() => {
        setTokenVersion((v) => v + 1);
      }),
    []
  );

  useEffect(() => {
    const url = process.env.REACT_APP_WS_URL;
    if (!isAuthenticated || !url) {
      return undefined;
    }

    let socket = null;
    let reconnectTimer = null;
    let attempt = 0;
    let disposed = false;

    const handleFrame = (raw) => {
      let frame;
      try {
        frame = JSON.parse(raw);
      } catch {
        return; // not JSON — nothing we understand
      }
      switch (frame?.type) {
        case "order_message": {
          const payload = frame?.payload;
          if (!payload?.id || !payload?.order_id) {
            return;
          }
          // No beep here: the `notification` frame for the same reply
          // follows right after and is the one that sounds (otherwise a
          // reply would beep twice).
          dispatch(socketMessage(payload));
          break;
        }
        case "order_message_read":
          dispatch(socketRead(frame?.payload));
          break;
        case "order_update":
          // No beep here either; the `notification` frame that follows
          // carries it. The Orders page re-renders on its own.
          dispatch(socketOrderUpdate(frame?.payload));
          break;
        case "notification": {
          const payload = frame?.payload;
          if (!payload?.id) {
            return;
          }
          // Beep once per new line, for both types, unless it is a message
          // for the very chat the customer is looking at.
          const openOrderId = store.getState()?.orderChat?.openOrderId;
          const alreadyKnown = store
            .getState()
            ?.notifications?.items?.some((n) => n?.id === payload.id);
          dispatch(socketNotification(payload));
          if (
            !alreadyKnown &&
            !(
              payload?.type === "order_message" &&
              openOrderId !== null &&
              openOrderId !== undefined &&
              openOrderId === payload?.order_id
            )
          ) {
            playBeep();
          }
          break;
        }
        case "notification_read":
          dispatch(socketNotificationRead(frame?.payload));
          break;
        default:
          break;
      }
    };

    const connect = () => {
      const token = LocalStorageService.get("authorizationData")?.access;
      if (!token) {
        // Nothing to authenticate with. A token change re-runs this effect.
        return;
      }
      // Same handshake as the admin app: the token rides in the subprotocol
      // list and the server accepts with subprotocol "Authorization".
      socket = new WebSocket(url, ["Authorization", token]);
      socket.onopen = () => {
        attempt = 0;
        if (hasOpenedRef.current) {
          dispatch(
            getNotifications({ page: 1, per_page: NOTIFICATIONS_PER_PAGE })
          );
        }
        hasOpenedRef.current = true;
      };
      socket.onmessage = (event) => {
        handleFrame(event?.data);
      };
      socket.onerror = () => {
        // onclose always follows an error; the reconnect logic lives there.
      };
      socket.onclose = (event) => {
        if (disposed) {
          return;
        }
        if (
          event?.code === CLOSE_UNAUTHORIZED ||
          event?.code === CLOSE_FORBIDDEN
        ) {
          return;
        }
        reconnectTimer = setTimeout(connect, getReconnectDelay(attempt));
        attempt += 1;
      };
    };

    connect();

    return () => {
      disposed = true;
      clearTimeout(reconnectTimer);
      if (socket) {
        socket.close();
      }
    };
  }, [isAuthenticated, tokenVersion, dispatch, store]);
}
